import React from "react";

/**
 * 20 Bespoke Apple Memoji & Marketing Suite 3D Matte Bisque Profession Avatars (`preset:avatar-01` .. `preset:avatar-20`)
 * Clean, simple, expressive 3D-sculpted Memoji faces on soft Scandinavian studio gallery backdrops,
 * with distinctive per-profession styling (Architect, Chef, Doctor, Engineer, Lawyer, Designer, etc.)
 * and deterministic zero-collision assignment within an organization.
 *
 * Canonical AI generation prompts & token specs for all 20 personas:
 * @see kb/specs/design/prompts/avatar_personas.md
 */
export const PRESET_AVATARS = [
  {
    id: "preset:avatar-01",
    index: 1,
    name: "Architect",
    label: "Architect",
    profession: "Architect",
    bgStart: "#FAF6F2",
    bgMid: "#F3EAE1",
    bgEnd: "#E6D7C9",
    accent: "#C85A32",
    badge: "#D97706",
    skinLight: "#FCE3CE",
    skin: "#F3C6A2",
    skinShadow: "#D49A70",
    skinDeep: "#B3774E",
    hairColor: "#231915",
    hairHighlight: "#4A362D",
    hair: "textured-quiff",
    eyeColor: "#4A3525",
    eyes: "confident",
    mouth: "warm-smile",
    lipTone: "#C47864",
    facialHair: "light-stubble",
    headgear: "none",
    glasses: "acetate-architect",
    earAccessory: "none",
    outfit: "architect-turtleneck",
  },
  {
    id: "preset:avatar-02",
    index: 2,
    name: "Executive Chef",
    label: "Executive Chef",
    profession: "Chef",
    bgStart: "#F3FAF7",
    bgMid: "#E5F3EC",
    bgEnd: "#D2E7DD",
    accent: "#10B981",
    badge: "#059669",
    skinLight: "#FADAC0",
    skin: "#ECBC94",
    skinShadow: "#CA9167",
    skinDeep: "#A66E45",
    hairColor: "#331F14",
    hairHighlight: "#5E3B27",
    hair: "swept-waves",
    eyeColor: "#3B5238",
    eyes: "warm",
    mouth: "gentle-grin",
    lipTone: "#BF7360",
    facialHair: "trimmed-beard",
    headgear: "none",
    glasses: "none",
    earAccessory: "none",
    outfit: "executive-chef-collar",
  },
  {
    id: "preset:avatar-03",
    index: 3,
    name: "Medical Doctor",
    label: "Medical Doctor",
    profession: "Doctor",
    bgStart: "#F4F8FB",
    bgMid: "#E6EFF7",
    bgEnd: "#D4E3F0",
    accent: "#0284C7",
    badge: "#38BDF8",
    skinLight: "#F5CBB0",
    skin: "#E3A982",
    skinShadow: "#BF8158",
    skinDeep: "#995E38",
    hairColor: "#1A1721",
    hairHighlight: "#3F384F",
    hair: "sleek-crown-bun",
    eyeColor: "#36261B",
    eyes: "lashes",
    mouth: "soft-smile",
    lipTone: "#B85D56",
    facialHair: "none",
    headgear: "none",
    glasses: "titanium-round",
    earAccessory: "pearl-stud",
    outfit: "clinical-coat-scrubs",
  },
  {
    id: "preset:avatar-04",
    index: 4,
    name: "Software Engineer",
    label: "Software Engineer",
    profession: "Engineer",
    bgStart: "#F4F5FB",
    bgMid: "#E8EAF6",
    bgEnd: "#D6DAF0",
    accent: "#6366F1",
    badge: "#4F46E5",
    skinLight: "#FDE6D0",
    skin: "#F4CEAC",
    skinShadow: "#D5A47C",
    skinDeep: "#B07E57",
    hairColor: "#1B202B",
    hairHighlight: "#3E485E",
    hair: "modern-crop",
    eyeColor: "#2C4C63",
    eyes: "focused",
    mouth: "warm-smile",
    lipTone: "#C77D6B",
    facialHair: "none",
    headgear: "none",
    glasses: "sleek-rect",
    earAccessory: "studio-headphones",
    outfit: "tech-quarter-zip",
  },
  {
    id: "preset:avatar-05",
    index: 5,
    name: "Master Electrician",
    label: "Master Electrician",
    profession: "Electrician",
    bgStart: "#FBF8F1",
    bgMid: "#F4ECDC",
    bgEnd: "#E6D9C0",
    accent: "#D97706",
    badge: "#F59E0B",
    skinLight: "#DCA47A",
    skin: "#C48659",
    skinShadow: "#9E6238",
    skinDeep: "#7A4520",
    hairColor: "#171311",
    hairHighlight: "#3B312C",
    hair: "taper-fade",
    eyeColor: "#332215",
    eyes: "confident",
    mouth: "gentle-grin",
    lipTone: "#9E5545",
    facialHair: "boxed-beard",
    headgear: "none",
    glasses: "none",
    earAccessory: "none",
    outfit: "technical-work-jacket",
  },
  {
    id: "preset:avatar-06",
    index: 6,
    name: "Interior Designer",
    label: "Interior Designer",
    profession: "Designer",
    bgStart: "#FBF4F6",
    bgMid: "#F5E6EA",
    bgEnd: "#E9D2D9",
    accent: "#E11D48",
    badge: "#FB7185",
    skinLight: "#FDE4CE",
    skin: "#F3C9A8",
    skinShadow: "#D29F79",
    skinDeep: "#AD7651",
    hairColor: "#5A2318",
    hairHighlight: "#8F3D2C",
    hair: "sculpted-bob",
    eyeColor: "#2D5A4C",
    eyes: "lashes",
    mouth: "rose-smile",
    lipTone: "#C9545C",
    facialHair: "none",
    headgear: "none",
    glasses: "tortoise-round",
    earAccessory: "gold-hoop",
    outfit: "atelier-silk-collar",
  },
  {
    id: "preset:avatar-07",
    index: 7,
    name: "Airline Captain",
    label: "Airline Captain",
    profession: "Pilot",
    bgStart: "#F3F7FC",
    bgMid: "#E5EEF8",
    bgEnd: "#D2E0F0",
    accent: "#1D4ED8",
    badge: "#F59E0B",
    skinLight: "#F7CEAC",
    skin: "#E7B288",
    skinShadow: "#C2895E",
    skinDeep: "#9C643D",
    hairColor: "#261D18",
    hairHighlight: "#524037",
    hair: "executive-part",
    eyeColor: "#264E70",
    eyes: "confident",
    mouth: "warm-smile",
    lipTone: "#B86B58",
    facialHair: "light-stubble",
    headgear: "none",
    glasses: "navigator-gold",
    earAccessory: "none",
    outfit: "captain-epaulet-suit",
  },
  {
    id: "preset:avatar-08",
    index: 8,
    name: "Legal Counsel",
    label: "Legal Counsel",
    profession: "Lawyer",
    bgStart: "#F6F4FB",
    bgMid: "#ECE8F6",
    bgEnd: "#DDD6EE",
    accent: "#7C3AED",
    badge: "#8B5CF6",
    skinLight: "#EAB48C",
    skin: "#D6976A",
    skinShadow: "#B07045",
    skinDeep: "#8A4F29",
    hairColor: "#1F1D24",
    hairHighlight: "#474352",
    hair: "silver-temples",
    eyeColor: "#382618",
    eyes: "focused",
    mouth: "warm-smile",
    lipTone: "#A85D4D",
    facialHair: "trimmed-beard",
    headgear: "none",
    glasses: "browline-gold",
    earAccessory: "none",
    outfit: "counsel-bespoke-suit",
  },
  {
    id: "preset:avatar-09",
    index: 9,
    name: "Landscape Botanist",
    label: "Landscape Botanist",
    profession: "Botanist",
    bgStart: "#F4FAF5",
    bgMid: "#E6F3E8",
    bgEnd: "#D3E7D7",
    accent: "#16A34A",
    badge: "#22C55E",
    skinLight: "#FCDCC4",
    skin: "#F1C09B",
    skinShadow: "#CF956B",
    skinDeep: "#AB6F46",
    hairColor: "#7C3316",
    hairHighlight: "#B04E28",
    hair: "soft-layered-waves",
    eyeColor: "#365E3D",
    eyes: "lashes",
    mouth: "soft-smile",
    lipTone: "#C4665B",
    facialHair: "subtle-freckles",
    headgear: "none",
    glasses: "none",
    earAccessory: "botanical-stud",
    outfit: "botanist-linen-jacket",
  },
  {
    id: "preset:avatar-10",
    index: 10,
    name: "Property Inspector",
    label: "Property Inspector",
    profession: "Inspector",
    bgStart: "#FAF7F2",
    bgMid: "#F2EBDD",
    bgEnd: "#E4D9C5",
    accent: "#B45309",
    badge: "#D97706",
    skinLight: "#F9D8BC",
    skin: "#ECBB95",
    skinShadow: "#C99268",
    skinDeep: "#A36C44",
    hairColor: "#36261E",
    hairHighlight: "#614639",
    hair: "executive-part",
    eyeColor: "#4F3B28",
    eyes: "arch-brow",
    mouth: "subtle-smirk",
    lipTone: "#BD6F5C",
    facialHair: "none",
    headgear: "none",
    glasses: "titanium-rect",
    earAccessory: "none",
    outfit: "inspector-trench-collar",
  },
  {
    id: "preset:avatar-11",
    index: 11,
    name: "Real Estate Broker",
    label: "Real Estate Broker",
    profession: "Broker",
    bgStart: "#F3F8FC",
    bgMid: "#E5EFF7",
    bgEnd: "#D3E2EE",
    accent: "#0369A1",
    badge: "#0284C7",
    skinLight: "#F7D1B4",
    skin: "#E8B692",
    skinShadow: "#C48E66",
    skinDeep: "#9E6842",
    hairColor: "#1F1713",
    hairHighlight: "#4A382F",
    hair: "textured-quiff",
    eyeColor: "#2D4C63",
    eyes: "confident",
    mouth: "gentle-grin",
    lipTone: "#BA6C59",
    facialHair: "none",
    headgear: "none",
    glasses: "none",
    earAccessory: "none",
    outfit: "broker-navy-blazer",
  },
  {
    id: "preset:avatar-12",
    index: 12,
    name: "Financial Director",
    label: "Financial Director",
    profession: "Finance",
    bgStart: "#F3FAF9",
    bgMid: "#E4F2F0",
    bgEnd: "#D1E6E3",
    accent: "#0D9488",
    badge: "#14B8A6",
    skinLight: "#FDE2C8",
    skin: "#F2C5A0",
    skinShadow: "#CF9870",
    skinDeep: "#A8714A",
    hairColor: "#382B24",
    hairHighlight: "#634E42",
    hair: "sleek-side-part",
    eyeColor: "#395249",
    eyes: "focused",
    mouth: "warm-smile",
    lipTone: "#C27462",
    facialHair: "none",
    headgear: "none",
    glasses: "rimless-executive",
    earAccessory: "none",
    outfit: "finance-charcoal-suit",
  },
  {
    id: "preset:avatar-13",
    index: 13,
    name: "Studio Photographer",
    label: "Studio Photographer",
    profession: "Photographer",
    bgStart: "#FAF5FB",
    bgMid: "#F2E7F4",
    bgEnd: "#E5D5E8",
    accent: "#C026D3",
    badge: "#D97706",
    skinLight: "#CC8E62",
    skin: "#B47347",
    skinShadow: "#8E532C",
    skinDeep: "#693818",
    hairColor: "#161117",
    hairHighlight: "#3A2D3D",
    hair: "sculpted-curls",
    eyeColor: "#2B1C14",
    eyes: "lashes",
    mouth: "rose-smile",
    lipTone: "#964644",
    facialHair: "none",
    headgear: "none",
    glasses: "amber-studio",
    earAccessory: "silver-stud",
    outfit: "creative-director-mockneck",
  },
  {
    id: "preset:avatar-14",
    index: 14,
    name: "Maintenance Pro",
    label: "Maintenance Pro",
    profession: "Plumber",
    bgStart: "#F4F7FC",
    bgMid: "#E6EEF8",
    bgEnd: "#D4E0F0",
    accent: "#2563EB",
    badge: "#3B82F6",
    skinLight: "#F8CFA8",
    skin: "#E9B288",
    skinShadow: "#C48A5F",
    skinDeep: "#9E643C",
    hairColor: "#422818",
    hairHighlight: "#70462C",
    hair: "modern-crop",
    eyeColor: "#3D2B1C",
    eyes: "warm",
    mouth: "warm-smile",
    lipTone: "#B86B58",
    facialHair: "tailored-mustache",
    headgear: "none",
    glasses: "none",
    earAccessory: "none",
    outfit: "specialist-chambray-collar",
  },
  {
    id: "preset:avatar-15",
    index: 15,
    name: "Fire & Safety Chief",
    label: "Fire & Safety Chief",
    profession: "Firefighter",
    bgStart: "#FBF5F5",
    bgMid: "#F5E6E6",
    bgEnd: "#E9D3D3",
    accent: "#DC2626",
    badge: "#F59E0B",
    skinLight: "#E5AA81",
    skin: "#CE8D61",
    skinShadow: "#A8683F",
    skinDeep: "#804926",
    hairColor: "#1A1513",
    hairHighlight: "#3D332F",
    hair: "taper-fade",
    eyeColor: "#2E1F14",
    eyes: "confident",
    mouth: "warm-smile",
    lipTone: "#A35747",
    facialHair: "light-stubble",
    headgear: "none",
    glasses: "none",
    earAccessory: "none",
    outfit: "safety-chief-dress-collar",
  },
  {
    id: "preset:avatar-16",
    index: 16,
    name: "Lab Scientist",
    label: "Lab Scientist",
    profession: "Scientist",
    bgStart: "#F3FAFB",
    bgMid: "#E4F3F6",
    bgEnd: "#D1E7EC",
    accent: "#0891B2",
    badge: "#06B6D4",
    skinLight: "#FDE4CB",
    skin: "#F2C8A4",
    skinShadow: "#CF9D76",
    skinDeep: "#A87550",
    hairColor: "#2C1629",
    hairHighlight: "#5C2F56",
    hair: "sleek-crown-bun",
    eyeColor: "#264D59",
    eyes: "lashes",
    mouth: "soft-smile",
    lipTone: "#C46862",
    facialHair: "none",
    headgear: "none",
    glasses: "clear-lab-frames",
    earAccessory: "pearl-stud",
    outfit: "research-director-coat",
  },
  {
    id: "preset:avatar-17",
    index: 17,
    name: "Artisan Barista",
    label: "Artisan Barista",
    profession: "Barista",
    bgStart: "#FAF6F2",
    bgMid: "#F2E8DE",
    bgEnd: "#E4D5C5",
    accent: "#9A3412",
    badge: "#D97706",
    skinLight: "#F4C59E",
    skin: "#E2AB80",
    skinShadow: "#BD8358",
    skinDeep: "#965F38",
    hairColor: "#2B1B12",
    hairHighlight: "#573927",
    hair: "sculpted-curls",
    eyeColor: "#422C1B",
    eyes: "warm",
    mouth: "gentle-grin",
    lipTone: "#B26352",
    facialHair: "boxed-beard",
    headgear: "none",
    glasses: "titanium-round",
    earAccessory: "none",
    outfit: "roaster-denim-apron",
  },
  {
    id: "preset:avatar-18",
    index: 18,
    name: "Grand Concierge",
    label: "Grand Concierge",
    profession: "Concierge",
    bgStart: "#F8F5FB",
    bgMid: "#EFE7F6",
    bgEnd: "#E0D4EC",
    accent: "#9333EA",
    badge: "#D97706",
    skinLight: "#FBDABE",
    skin: "#EEC29E",
    skinShadow: "#CC9870",
    skinDeep: "#A6714A",
    hairColor: "#1F1821",
    hairHighlight: "#46374A",
    hair: "sculpted-bob",
    eyeColor: "#38293B",
    eyes: "lashes",
    mouth: "rose-smile",
    lipTone: "#C25B61",
    facialHair: "none",
    headgear: "none",
    glasses: "none",
    earAccessory: "gold-hoop",
    outfit: "hospitality-lapel-suit",
  },
  {
    id: "preset:avatar-19",
    index: 19,
    name: "University Professor",
    label: "University Professor",
    profession: "Professor",
    bgStart: "#F7FAF3",
    bgMid: "#ECF2E4",
    bgEnd: "#DCE5D0",
    accent: "#65A30D",
    badge: "#84CC16",
    skinLight: "#F7D1B2",
    skin: "#E7B892",
    skinShadow: "#C48F67",
    skinDeep: "#9E6943",
    hairColor: "#c7c4c1",
    hairHighlight: "#f0efed",
    hair: "distinguished-waves",
    eyeColor: "#3D4D35",
    eyes: "warm",
    mouth: "warm-smile",
    lipTone: "#B56C5B",
    facialHair: "silver-beard",
    headgear: "none",
    glasses: "tortoise-round",
    earAccessory: "none",
    outfit: "professor-tweed-collar",
  },
  {
    id: "preset:avatar-20",
    index: 20,
    name: "Civil Engineer",
    label: "Civil Engineer",
    profession: "Civil Eng",
    bgStart: "#FBF6F2",
    bgMid: "#F4E9DF",
    bgEnd: "#E7D6C7",
    accent: "#EA580C",
    badge: "#FB923C",
    skinLight: "#C28055",
    skin: "#A8663D",
    skinShadow: "#854A26",
    skinDeep: "#613114",
    hairColor: "#141210",
    hairHighlight: "#38322C",
    hair: "soft-layered-waves",
    eyeColor: "#261810",
    eyes: "lashes",
    mouth: "gentle-grin",
    lipTone: "#8F453F",
    facialHair: "none",
    headgear: "none",
    glasses: "sleek-rect",
    earAccessory: "gold-hoop",
    outfit: "structural-blazer-collar",
  },
];

export const PRESET_AVATAR_IDS = PRESET_AVATARS.map((a) => a.id);

export const LEGACY_PRESET_AVATAR_IDS = PRESET_AVATAR_IDS;

const PRESET_MAP = new Map(PRESET_AVATARS.map((a) => [a.id, a]));

export function isPresetAvatarId(value) {
  return typeof value === "string" && PRESET_MAP.has(value.trim());
}

export function getPresetAvatar(idOrIndex) {
  if (typeof idOrIndex === "number") {
    const idx =
      (((idOrIndex - 1) % PRESET_AVATARS.length) + PRESET_AVATARS.length) %
      PRESET_AVATARS.length;
    return PRESET_AVATARS[idx];
  }
  if (typeof idOrIndex === "string") {
    const trimmed = idOrIndex.trim();
    if (PRESET_MAP.has(trimmed)) {
      return PRESET_MAP.get(trimmed);
    }
    const normalized = trimmed.startsWith("preset:")
      ? trimmed
      : `preset:${trimmed}`;
    if (PRESET_MAP.has(normalized)) {
      return PRESET_MAP.get(normalized);
    }
  }
  return PRESET_AVATARS[0];
}

export const getPresetAvatarById = getPresetAvatar;

/**
 * Deterministic 32-bit FNV-1a hash for strings
 */
export function hashAvatarSeed(input) {
  const str = String(input || "homy-default-user");
  let hash = 2166136261;
  for (let i = 0; i < str.length; i += 1) {
    hash ^= str.charCodeAt(i);
    hash = Math.imul(hash, 16777619);
  }
  return hash >>> 0;
}

/**
 * Picks a deterministic preset avatar for a user, ensuring no collision with `usedPresetIds`
 * within the same organization whenever slots remain available.
 */
export function resolveOrgUniquePreset(
  userKey,
  orgId = "default-org",
  usedPresetIds = new Set(),
) {
  const used =
    usedPresetIds instanceof Set
      ? usedPresetIds
      : new Set(usedPresetIds || []);
  const seed = hashAvatarSeed(`${orgId}::${userKey}`);
  const total = PRESET_AVATARS.length;
  // Coprime step (7 is coprime with 20) guarantees visiting all 20 slots deterministically
  const step = 7;
  for (let attempt = 0; attempt < total; attempt += 1) {
    const idx = (seed + attempt * step) % total;
    const candidate = PRESET_AVATARS[idx].id;
    if (!used.has(candidate)) {
      return candidate;
    }
  }
  return PRESET_AVATARS[seed % total].id;
}

export const getDeterministicAvatarId = resolveOrgUniquePreset;

/**
 * Given a list of user objects in an organization, returns a Map of `userKey -> presetId`
 * (or custom photo URL if the user has a non-preset photo), guaranteeing no two users in the
 * same organization share the same preset avatar (up to 20 users per org).
 */
export function assignUniqueAvatarsForOrg(users = [], orgId = "default-org") {
  const result = new Map();
  const usedPresets = new Set();

  // Pass 1: Honor existing valid unique preset selections
  for (const user of users) {
    if (!user) continue;
    const key =
      user.id ||
      user.userId ||
      user.email ||
      user.fullName ||
      user.full_name ||
      user.name;
    const pic =
      user.pictureUrl || user.picture_url || user.avatarUrl || user.avatar;
    if (pic && !isPresetAvatarId(pic)) {
      result.set(key, pic);
    } else if (isPresetAvatarId(pic) && !usedPresets.has(pic)) {
      usedPresets.add(pic);
      result.set(key, pic);
    }
  }

  // Pass 2: Assign org-unique presets to remaining users (or resolve duplicates)
  for (const user of users) {
    if (!user) continue;
    const key =
      user.id ||
      user.userId ||
      user.email ||
      user.fullName ||
      user.full_name ||
      user.name;
    if (result.has(key)) continue;
    const assigned = resolveOrgUniquePreset(key, orgId, usedPresets);
    usedPresets.add(assigned);
    result.set(key, assigned);
  }

  return result;
}

export const resolveOrgUniqueAvatars = assignUniqueAvatarsForOrg;

/**
 * Back hair layer for Apple Memoji-style 3D sculpted portraits.
 */
function renderBackHair(hair, hairGradUrl, hairColor) {
  switch (hair) {
    case "sleek-crown-bun":
      return (
        <g>
          {/* High sculpted Apple Memoji top-bun with 3D strand grooves */}
          <ellipse cx="32" cy="7" rx="8.5" ry="6" fill={hairGradUrl} />
          <path
            d="M25.5 7.2C27.8 4.2 36.2 4.2 38.5 7.2"
            stroke="#ffffff"
            strokeOpacity="0.24"
            strokeWidth="1.2"
            strokeLinecap="round"
          />
          <path
            d="M27.5 5C30.2 3.6 33.8 3.6 36.5 5"
            stroke="#000000"
            strokeOpacity="0.18"
            strokeWidth="1"
            strokeLinecap="round"
          />
          <path
            d="M16.2 27C16.2 13.6 22.8 9 32 9C41.2 9 47.8 13.6 47.8 27"
            fill={hairColor}
          />
        </g>
      );
    case "sculpted-bob":
      return (
        <g>
          {/* Rounded chin-length Apple Memoji bob tucked smoothly behind ears */}
          <path
            d="M14.2 27.5C13.2 13.5 20.5 7.8 32 7.8C43.5 7.8 50.8 13.5 49.8 27.5C50.5 36.8 48.2 44.2 43.2 46.4H20.8C15.8 44.2 13.5 36.8 14.2 27.5Z"
            fill={hairGradUrl}
          />
          <path
            d="M15.8 31C15.2 37.2 17.2 42.8 20.8 45M48.2 31C48.8 37.2 46.8 42.8 43.2 45"
            stroke="#000000"
            strokeOpacity="0.18"
            strokeWidth="1.25"
            strokeLinecap="round"
          />
        </g>
      );
    case "soft-layered-waves":
      return (
        <g>
          {/* Flowing Apple Memoji wavy silhouette */}
          <path
            d="M13.6 28C12.4 13.5 19.8 7.5 32 7.5C44.2 7.5 51.6 13.5 50.4 28C51.6 37.4 49.2 46.2 44.6 50.4H19.4C14.8 46.2 12.4 37.4 13.6 28Z"
            fill={hairGradUrl}
          />
          <path
            d="M15.4 25.5C14.4 33.5 16 42 19.2 48M48.6 25.5C49.6 33.5 48 42 44.8 48"
            stroke="#ffffff"
            strokeOpacity="0.16"
            strokeWidth="1.35"
            strokeLinecap="round"
          />
        </g>
      );
    case "sculpted-curls":
      return (
        <g fill={hairGradUrl}>
          {/* Cloud-soft 3D Memoji curl silhouette */}
          <path d="M14.6 30C12 27.2 12 22.2 14.6 19.2C13.6 14.6 16.6 10 21.4 9C24.2 5.8 29.2 5 32 6.2C34.8 5 39.8 5.8 42.6 9C47.4 10 50.4 14.6 49.4 19.2C52 22.2 52 27.2 49.4 30C50.4 33.8 48.8 37.8 45.4 39.2H18.6C15.2 37.8 13.6 33.8 14.6 30Z" />
        </g>
      );
    case "distinguished-waves":
      return (
        <g fill={hairGradUrl}>
          {/* Voluminous silver-wave Memoji silhouette */}
          <path d="M14.6 26.8C13.4 13.6 20.4 7.8 32 7.8C43.6 7.8 50.6 13.6 49.4 26.8C50.2 33.2 48.6 38.4 45.8 40.4H18.2C15.4 38.4 13.8 33.2 14.6 26.8Z" />
        </g>
      );
    default:
      return (
        <path
          d="M16.2 26.5C15.8 13.8 22.2 7.8 32 7.8C41.8 7.8 48.2 13.8 47.8 26.5H16.2Z"
          fill={hairGradUrl}
        />
      );
  }
}

/**
 * Front hair & temple framing with smooth cubic Bézier Apple Memoji strand flow.
 */
function renderFrontHair(hair, hairGradUrl, hairColor, hairHighlight) {
  switch (hair) {
    case "textured-quiff":
      return (
        <g>
          <path
            d="M16.2 26.5C15.4 13 21.8 6.6 32.5 6.6C42.8 6.6 48.4 12.8 47.8 26.5C47.4 28.4 46.5 29.2 45.8 28.8C45.1 21.8 42 16.2 36.2 14.8C29.8 13.2 21.8 15.6 18.4 22.8C18 25.5 17.8 28.2 17.2 28.8C16.6 29.2 16.3 28 16.2 26.5Z"
            fill={hairGradUrl}
          />
          <path
            d="M22.2 11C27.6 8.4 36.4 8.6 41.6 11.6"
            stroke={hairHighlight}
            strokeOpacity="0.45"
            strokeWidth="1.8"
            strokeLinecap="round"
          />
        </g>
      );
    case "swept-waves":
      return (
        <g>
          <path
            d="M16 26.8C15.2 13.2 22 7 32 7C42.2 7 48.8 13.2 48 26.8C47.6 28.6 46.6 29.4 45.8 28.8C44.8 21 39.8 15.6 31.6 15C24 14.4 19.2 19.6 18.2 28.8C17.4 29.4 16.4 28.6 16 26.8Z"
            fill={hairGradUrl}
          />
          <path
            d="M21.5 11.6C27.2 9 36 9.4 41.8 12.6"
            stroke={hairHighlight}
            strokeOpacity="0.42"
            strokeWidth="1.7"
            strokeLinecap="round"
          />
        </g>
      );
    case "modern-crop":
      return (
        <g>
          <path
            d="M16.4 26.2C16 13.6 22.2 7.6 32 7.6C41.8 7.6 48 13.6 47.6 26.2C47.2 28 46.4 28.6 45.8 28.2C44.8 20 39.6 15.2 32 15.2C24.4 15.2 19.2 20 18.2 28.2C17.6 28.6 16.8 28 16.4 26.2Z"
            fill={hairGradUrl}
          />
          <path
            d="M23.5 11.2C28.2 9.4 35.8 9.4 40.5 11.2"
            stroke={hairHighlight}
            strokeOpacity="0.4"
            strokeWidth="1.6"
            strokeLinecap="round"
          />
        </g>
      );
    case "taper-fade":
      return (
        <g>
          <path
            d="M16.8 25.8C16.8 13.4 22.8 8 32 8C41.2 8 47.2 13.4 47.2 25.8C46.8 27.6 46.2 28.2 45.6 27.8C44.6 19.6 39.4 15.2 32 15.2C24.6 15.2 19.4 19.6 18.4 27.8C17.8 28.2 17.2 27.6 16.8 25.8Z"
            fill={hairGradUrl}
          />
          <path
            d="M24.5 11.2C28.8 9.6 35.2 9.6 39.5 11.2"
            stroke={hairHighlight}
            strokeOpacity="0.36"
            strokeWidth="1.5"
            strokeLinecap="round"
          />
        </g>
      );
    case "sculpted-bob":
      return (
        <g>
          {/* Side-parted Apple Memoji bob bang sweep */}
          <path
            d="M15.6 28C16.2 14 22.6 7.8 32 7.8C41.4 7.8 47.8 14 48.4 28C45.2 19.8 39.4 15 30.2 14.6C23.5 14.4 18.8 19.6 15.6 28Z"
            fill={hairGradUrl}
          />
          <path
            d="M22 11.8C27.2 9.4 36.2 9.4 41.5 12.4"
            stroke={hairHighlight}
            strokeOpacity="0.45"
            strokeWidth="1.7"
            strokeLinecap="round"
          />
        </g>
      );
    case "soft-layered-waves":
      return (
        <g>
          <path
            d="M15.6 28.2C16.2 14 22.6 7.8 32 7.8C41.4 7.8 47.8 14 48.4 28.2C44.8 20 39.8 15.2 32 14.8C24.2 15.2 19.2 20 15.6 28.2Z"
            fill={hairGradUrl}
          />
          <path
            d="M22 12C27.2 9.6 36.2 9.6 41.5 12.6"
            stroke={hairHighlight}
            strokeOpacity="0.44"
            strokeWidth="1.7"
            strokeLinecap="round"
          />
        </g>
      );
    case "sculpted-curls":
      return (
        <g>
          <path
            d="M16.2 25.8C15.8 18.8 18.2 12.8 23.5 10.4C26.5 8.8 30 8.4 32 9.4C34 8.4 37.5 8.8 40.5 10.4C45.8 12.8 48.2 18.8 47.8 25.8C45.5 21.2 42.5 18.2 38.8 17.2C36.2 16.4 34 17.2 32 16.2C30 17.2 27.8 16.4 25.2 17.2C21.5 18.2 18.5 21.2 16.2 25.8Z"
            fill={hairGradUrl}
          />
          <path
            d="M23.5 12C26.2 10.2 29.8 10.2 31.8 11.6M33.8 11.6C36.2 10.2 39.5 10.4 41.5 12.2"
            stroke={hairHighlight}
            strokeOpacity="0.44"
            strokeWidth="1.5"
            strokeLinecap="round"
          />
        </g>
      );
    case "silver-temples":
      return (
        <g>
          <path
            d="M16.4 26.4C16 13.6 22.2 7.4 32 7.4C41.8 7.4 48 13.6 47.6 26.4C47.2 28.2 46.4 28.8 45.6 28.4C44.8 20.2 39.8 15.2 31.2 15.2C24 15.2 19.2 20.2 18.4 28.4C17.6 28.8 16.8 28.2 16.4 26.4Z"
            fill={hairGradUrl}
          />
          {/* Distinguished silver temples */}
          <path
            d="M16.4 21.8C16.6 25.5 17.2 28.4 18.4 29.2L19.1 21.8H16.4ZM47.6 21.8C47.4 25.5 46.8 28.4 45.6 29.2L44.9 21.8H47.6Z"
            fill="#cbd5e1"
            fillOpacity="0.82"
          />
          <path
            d="M23 11.2C28.2 9 36.2 9 41.2 11.6"
            stroke="#e2e8f0"
            strokeOpacity="0.44"
            strokeWidth="1.6"
            strokeLinecap="round"
          />
        </g>
      );
    case "distinguished-waves":
      return (
        <g>
          <path
            d="M16.2 26.6C16.5 13.8 22.6 7.8 32 7.8C41.4 7.8 47.5 13.8 47.8 26.6C47.4 28.4 46.4 29 45.6 28.4C44.5 20.2 39.2 15.2 32 15.2C24.8 15.2 19.5 20.2 18.4 28.4C17.6 29 16.6 28.4 16.2 26.6Z"
            fill={hairGradUrl}
          />
          <path
            d="M22.5 11.4C27.8 9 36.2 9 41.5 11.4"
            stroke="#ffffff"
            strokeOpacity="0.62"
            strokeWidth="1.7"
            strokeLinecap="round"
          />
        </g>
      );
    case "sleek-crown-bun":
      return (
        <g>
          {/* Apple Memoji parted crown hairline with delicate temple framing */}
          <path
            d="M16.4 26.6C16.8 14 22.8 8.4 32 8.4C41.2 8.4 47.2 14 47.6 26.6C44.8 19.2 39.2 14.6 32 14.2C24.8 14.6 19.2 19.2 16.4 26.6Z"
            fill={hairGradUrl}
          />
          <path
            d="M23 11.8C28 9.6 36 9.6 41 11.8"
            stroke={hairHighlight}
            strokeOpacity="0.42"
            strokeWidth="1.6"
            strokeLinecap="round"
          />
        </g>
      );
    case "sleek-side-part":
    case "executive-part":
    default:
      return (
        <g>
          <path
            d="M16.4 26.6C16.2 13.8 22.4 7.6 32 7.6C41.6 7.6 47.8 13.8 47.6 26.6C47.2 28.4 46.3 29 45.5 28.4C44.5 20.6 39.2 15.8 29.8 15.8C24 15.8 19.4 20.4 18.5 28.4C17.7 29 16.8 28.4 16.4 26.6Z"
            fill={hairGradUrl}
          />
          <path
            d="M23 11.4C28.2 9 36.2 9.2 41.4 11.8"
            stroke={hairHighlight}
            strokeOpacity="0.44"
            strokeWidth="1.7"
            strokeLinecap="round"
          />
        </g>
      );
  }
}

/**
 * Clean, expressive Apple Memoji eyes, rich iris with dual studio catchlights, and sculpted eyebrows.
 */
function renderStudioEyesAndBrows(eyeStyle, eyeColor, hairColor, skinShadow) {
  const leftX = 25.3;
  const rightX = 38.7;
  const eyeY = 28;
  const browColor = hairColor === "#c7c4c1" ? "#78716c" : hairColor;
  const irisColor = eyeColor || "#3b281c";

  return (
    <g>
      {/* Subtle upper eyelid crease */}
      <path
        d="M21.9 25.1C23.7 24.1 27 24.1 28.8 25.1"
        stroke={skinShadow}
        strokeOpacity="0.48"
        strokeWidth="0.8"
        strokeLinecap="round"
        fill="none"
      />
      <path
        d="M35.2 25.1C37 24.1 40.3 24.1 42.1 25.1"
        stroke={skinShadow}
        strokeOpacity="0.48"
        strokeWidth="0.8"
        strokeLinecap="round"
        fill="none"
      />

      {/* Crisp Apple Memoji almond-round sclera */}
      <ellipse cx={leftX} cy={eyeY} rx="3.6" ry="2.65" fill="#ffffff" />
      <ellipse cx={rightX} cy={eyeY} rx="3.6" ry="2.65" fill="#ffffff" />

      {/* Rich Iris with dark limbal ring */}
      <circle
        cx={leftX + 0.05}
        cy={eyeY + 0.05}
        r="2.15"
        fill={irisColor}
        stroke="#141210"
        strokeWidth="0.32"
        strokeOpacity="0.65"
      />
      <circle
        cx={rightX + 0.05}
        cy={eyeY + 0.05}
        r="2.15"
        fill={irisColor}
        stroke="#141210"
        strokeWidth="0.32"
        strokeOpacity="0.65"
      />

      {/* Deep Pupil */}
      <circle cx={leftX + 0.05} cy={eyeY + 0.05} r="1.08" fill="#12100e" />
      <circle cx={rightX + 0.05} cy={eyeY + 0.05} r="1.08" fill="#12100e" />

      {/* Signature Apple Memoji Key Catchlight + Secondary Soft Bounce Reflection */}
      <circle cx={leftX - 0.65} cy={eyeY - 0.72} r="0.74" fill="#ffffff" />
      <circle cx={rightX - 0.65} cy={eyeY - 0.72} r="0.74" fill="#ffffff" />
      <circle
        cx={leftX + 0.75}
        cy={eyeY + 0.7}
        r="0.32"
        fill="#ffffff"
        fillOpacity="0.82"
      />
      <circle
        cx={rightX + 0.75}
        cy={eyeY + 0.7}
        r="0.32"
        fill="#ffffff"
        fillOpacity="0.82"
      />

      {/* Smooth Apple Memoji upper lash arch */}
      <path
        d="M21.5 27.8C22.7 25.2 28 25.2 29.2 27.8"
        stroke="#1c1613"
        strokeWidth={eyeStyle === "lashes" ? "1.45" : "1.2"}
        strokeLinecap="round"
        fill="none"
      />
      <path
        d="M34.8 27.8C36 25.2 41.3 25.2 42.5 27.8"
        stroke="#1c1613"
        strokeWidth={eyeStyle === "lashes" ? "1.45" : "1.2"}
        strokeLinecap="round"
        fill="none"
      />

      {/* Delicate outer lash flicks for "lashes" style */}
      {eyeStyle === "lashes" && (
        <g stroke="#1c1613" strokeWidth="1.2" strokeLinecap="round">
          <path d="M21.7 27.3L20.7 26.3" />
          <path d="M42.3 27.3L43.3 26.3" />
        </g>
      )}

      {/* Smooth Sculpted Apple Memoji Eyebrows positioned cleanly above eyewear rims */}
      {eyeStyle === "arch-brow" ? (
        <g stroke={browColor} strokeWidth="1.75" strokeLinecap="round" fill="none">
          <path d="M21.4 21.5C23.5 20.1 26.9 20.1 29.1 21.2" />
          <path d="M34.9 21C37.1 19.5 40.5 19.6 42.6 21.2" />
        </g>
      ) : (
        <g stroke={browColor} strokeWidth="1.75" strokeLinecap="round" fill="none">
          <path d="M21.4 21.4C23.6 20.1 27 20.1 29.1 21.2" />
          <path d="M34.9 21.2C37 20.1 40.4 20.1 42.6 21.4" />
        </g>
      )}
    </g>
  );
}

/**
 * Simple, soft 3D Apple Memoji button nose (no harsh nostril holes).
 */
function renderStudioNose(skinShadow, skinDeep) {
  return (
    <g>
      {/* Subtle 3D nose bridge contour */}
      <path
        d="M32.7 27.2L33.1 32.2C33.2 33.3 32.5 34 31.5 34"
        stroke={skinDeep || skinShadow}
        strokeOpacity="0.26"
        strokeWidth="1"
        strokeLinecap="round"
        fill="none"
      />
      {/* Soft rounded Apple Memoji button-nose base curve */}
      <path
        d="M30.2 33.5C31.1 34.4 32.9 34.4 33.8 33.5"
        stroke={skinDeep || skinShadow}
        strokeOpacity="0.5"
        strokeWidth="1.3"
        strokeLinecap="round"
        fill="none"
      />
      {/* Soft matte tip highlight */}
      <ellipse
        cx="31.9"
        cy="32.4"
        rx="1.15"
        ry="0.8"
        fill="#ffffff"
        fillOpacity="0.38"
      />
    </g>
  );
}

/**
 * Clean, friendly Apple Memoji smiles & smooth 3D facial hair.
 */
function renderFacialHairAndMouth(
  mouth,
  facialHair,
  hairColor,
  lipTone,
  skin,
  skinShadow,
) {
  const lipColor = lipTone || "#bd6b5a";
  const beardColor = facialHair === "silver-beard" ? "#9ca3af" : hairColor;

  return (
    <g>
      {/* Subtle natural cheek/nose freckles */}
      {facialHair === "subtle-freckles" && (
        <g fill="#a16207" fillOpacity="0.34">
          <circle cx="22.8" cy="32.4" r="0.48" />
          <circle cx="24.4" cy="33" r="0.52" />
          <circle cx="21.6" cy="33.4" r="0.45" />
          <circle cx="41.2" cy="32.4" r="0.48" />
          <circle cx="39.6" cy="33" r="0.52" />
          <circle cx="42.4" cy="33.4" r="0.45" />
        </g>
      )}

      {/* Soft Apple Memoji designer stubble */}
      {facialHair === "light-stubble" && (
        <path
          d="M17.8 33.2C18.2 40.6 23.8 46.2 32 46.2C40.2 46.2 45.8 40.6 46.2 33.2C43.8 36.8 39 38.4 32 38.4C25 38.4 20.2 36.8 17.8 33.2Z"
          fill={hairColor}
          fillOpacity="0.14"
        />
      )}

      {/* Smooth 3D Memoji beard hugging the jawline */}
      {(facialHair === "trimmed-beard" ||
        facialHair === "boxed-beard" ||
        facialHair === "silver-beard") && (
        <g>
          <path
            d="M17.4 31C17.6 40.2 23.4 46.5 32 46.5C40.6 46.5 46.4 40.2 46.6 31L45 31C44.2 35.2 41 37 37.5 36.6C35.8 35.6 34 35.2 32 35.2C30 35.2 28.2 35.6 26.5 36.6C23 37 19.8 35.2 19 31L17.4 31Z"
            fill={beardColor}
            fillOpacity={facialHair === "trimmed-beard" ? "0.82" : "0.9"}
          />
          {/* Smooth rounded mouth opening inside beard */}
          <ellipse
            cx="32"
            cy="39.2"
            rx="5.8"
            ry="2.9"
            fill={skin || skinShadow}
          />
        </g>
      )}

      {/* Friendly Apple Memoji Open Grin */}
      {mouth === "gentle-grin" && (
        <g>
          <path
            d="M27.3 37.9C29.5 38.4 34.5 38.4 36.7 37.9C36 40.9 34.3 41.9 32 41.9C29.7 41.9 28 40.9 27.3 37.9Z"
            fill={lipColor}
          />
          <path
            d="M28 38.1C29.8 38.5 34.2 38.5 36 38.1C35.5 39.6 34 40.2 32 40.2C30 40.2 28.5 39.6 28 38.1Z"
            fill="#ffffff"
          />
        </g>
      )}

      {/* Sculpted Rose Smile */}
      {mouth === "rose-smile" && (
        <g>
          <path
            d="M27.6 38.4C29.6 40.4 34.4 40.4 36.4 38.4C35.4 40.5 33.9 41.3 32 41.3C30.1 41.3 28.6 40.5 27.6 38.4Z"
            fill={lipColor}
            fillOpacity="0.88"
          />
          <path
            d="M27.4 38.3C29.5 40.1 34.5 40.1 36.6 38.3"
            stroke="#6b2128"
            strokeOpacity="0.75"
            strokeWidth="1.4"
            strokeLinecap="round"
            fill="none"
          />
        </g>
      )}

      {/* Confident Memoji Half-Smile */}
      {mouth === "subtle-smirk" && (
        <g>
          <path
            d="M27.8 38.7C30.4 39.8 34.2 39.2 36.6 37.7"
            stroke="#6e3429"
            strokeWidth="1.5"
            strokeLinecap="round"
            fill="none"
          />
          <path
            d="M30 40.5C31.2 40.9 32.8 40.9 34 40.4"
            stroke={skinShadow}
            strokeOpacity="0.5"
            strokeWidth="0.95"
            strokeLinecap="round"
            fill="none"
          />
        </g>
      )}

      {/* Classic Warm Apple Memoji Smile */}
      {(mouth === "warm-smile" || mouth === "soft-smile" || !mouth) && (
        <g>
          <path
            d="M27.8 38.5C29.6 40.3 34.4 40.3 36.2 38.5C35.2 40.3 33.8 41 32 41C30.2 41 28.8 40.3 27.8 38.5Z"
            fill={lipColor}
            fillOpacity="0.72"
          />
          <path
            d="M27.4 38.3C29.5 40.2 34.5 40.2 36.6 38.3"
            stroke="#6e3429"
            strokeWidth="1.45"
            strokeLinecap="round"
            fill="none"
          />
        </g>
      )}

      {/* Tailored Memoji Mustache */}
      {facialHair === "tailored-mustache" && (
        <path
          d="M32 35.8C29.5 35.2 26.8 36 25.8 37.5C27.8 37.9 30.2 37.5 32 36.9C33.8 37.5 36.2 37.9 38.2 37.5C37.2 36 34.5 35.2 32 35.8Z"
          fill={hairColor}
        />
      )}
    </g>
  );
}

/**
 * Iconic Apple Memoji eyewear frames (generous round & rounded-rect frames with clear brow separation).
 */
function renderGlasses(glasses) {
  switch (glasses) {
    case "acetate-architect":
      return (
        <g>
          <circle
            cx="25.1"
            cy="28"
            r="4.95"
            fill="#ffffff"
            fillOpacity="0.08"
            stroke="#1c1917"
            strokeWidth="1.7"
          />
          <circle
            cx="38.9"
            cy="28"
            r="4.95"
            fill="#ffffff"
            fillOpacity="0.08"
            stroke="#1c1917"
            strokeWidth="1.7"
          />
          <path d="M30.1 27.5H33.9" stroke="#1c1917" strokeWidth="1.6" />
          <path
            d="M16.8 27.2L20.1 27.6M43.9 27.6L47.2 27.2"
            stroke="#1c1917"
            strokeWidth="1.35"
            strokeLinecap="round"
          />
          <path
            d="M22.2 25.4L24.2 28.8M36 25.4L38 28.8"
            stroke="#ffffff"
            strokeOpacity="0.28"
            strokeWidth="1.1"
            strokeLinecap="round"
          />
        </g>
      );
    case "sleek-rect":
      return (
        <g>
          <rect
            x="19.6"
            y="24.3"
            width="11"
            height="7.3"
            rx="2.7"
            fill="#38bdf8"
            fillOpacity="0.08"
            stroke="#273244"
            strokeWidth="1.5"
          />
          <rect
            x="33.4"
            y="24.3"
            width="11"
            height="7.3"
            rx="2.7"
            fill="#38bdf8"
            fillOpacity="0.08"
            stroke="#273244"
            strokeWidth="1.5"
          />
          <path d="M30.6 27.2H33.4" stroke="#273244" strokeWidth="1.4" />
          <path
            d="M16.8 27H19.6M44.4 27H47.2"
            stroke="#273244"
            strokeWidth="1.25"
          />
          <path
            d="M21.4 25.6L23.4 29M35.2 25.6L37.2 29"
            stroke="#ffffff"
            strokeOpacity="0.25"
            strokeWidth="1.05"
            strokeLinecap="round"
          />
        </g>
      );
    case "tortoise-round":
      return (
        <g>
          <circle
            cx="25.1"
            cy="28"
            r="4.95"
            fill="#ffffff"
            fillOpacity="0.08"
            stroke="#6b3410"
            strokeWidth="1.65"
          />
          <circle
            cx="38.9"
            cy="28"
            r="4.95"
            fill="#ffffff"
            fillOpacity="0.08"
            stroke="#6b3410"
            strokeWidth="1.65"
          />
          <path d="M30.1 27.5H33.9" stroke="#6b3410" strokeWidth="1.5" />
          <path
            d="M16.8 27.3H20.1M43.9 27.3H47.2"
            stroke="#6b3410"
            strokeWidth="1.3"
          />
          <path
            d="M22.2 25.4L24.2 28.8M36 25.4L38 28.8"
            stroke="#ffffff"
            strokeOpacity="0.25"
            strokeWidth="1.05"
            strokeLinecap="round"
          />
        </g>
      );
    case "titanium-round":
      return (
        <g>
          <circle
            cx="25.1"
            cy="28"
            r="4.95"
            fill="#ffffff"
            fillOpacity="0.08"
            stroke="#2b2623"
            strokeWidth="1.55"
          />
          <circle
            cx="38.9"
            cy="28"
            r="4.95"
            fill="#ffffff"
            fillOpacity="0.08"
            stroke="#2b2623"
            strokeWidth="1.55"
          />
          <path d="M30.1 27.5H33.9" stroke="#2b2623" strokeWidth="1.45" />
          <path
            d="M16.8 27.3H20.1M43.9 27.3H47.2"
            stroke="#2b2623"
            strokeWidth="1.25"
          />
          <path
            d="M22.2 25.4L24.2 28.8M36 25.4L38 28.8"
            stroke="#ffffff"
            strokeOpacity="0.26"
            strokeWidth="1.05"
            strokeLinecap="round"
          />
        </g>
      );
    case "browline-gold":
      return (
        <g>
          <rect
            x="19.6"
            y="24.4"
            width="11"
            height="7.1"
            rx="2.5"
            fill="#ffffff"
            fillOpacity="0.07"
            stroke="#d4af37"
            strokeWidth="1.2"
          />
          <rect
            x="33.4"
            y="24.4"
            width="11"
            height="7.1"
            rx="2.5"
            fill="#ffffff"
            fillOpacity="0.07"
            stroke="#d4af37"
            strokeWidth="1.2"
          />
          <path
            d="M19.4 24.7H30.8M33.2 24.7H44.6"
            stroke="#1e1b24"
            strokeWidth="1.9"
            strokeLinecap="round"
          />
          <path
            d="M30.6 27H33.4M17 26.6H19.6M44.4 26.6H47"
            stroke="#d4af37"
            strokeWidth="1.2"
          />
        </g>
      );
    case "rimless-executive":
      return (
        <g>
          <rect
            x="19.8"
            y="24.5"
            width="10.8"
            height="6.9"
            rx="2.3"
            fill="#ffffff"
            fillOpacity="0.1"
            stroke="#64748b"
            strokeWidth="1.15"
          />
          <rect
            x="33.4"
            y="24.5"
            width="10.8"
            height="6.9"
            rx="2.3"
            fill="#ffffff"
            fillOpacity="0.1"
            stroke="#64748b"
            strokeWidth="1.15"
          />
          <path
            d="M30.6 27.1H33.4M17 27.1H19.8M44.2 27.1H47"
            stroke="#64748b"
            strokeWidth="1.15"
          />
        </g>
      );
    case "navigator-gold":
      return (
        <g>
          <rect
            x="19.6"
            y="24.4"
            width="11"
            height="7.2"
            rx="2.7"
            fill="#0f172a"
            fillOpacity="0.14"
            stroke="#d97706"
            strokeWidth="1.35"
          />
          <rect
            x="33.4"
            y="24.4"
            width="11"
            height="7.2"
            rx="2.7"
            fill="#0f172a"
            fillOpacity="0.14"
            stroke="#d97706"
            strokeWidth="1.35"
          />
          <path
            d="M30.5 25.3H33.5M30.6 27.1H33.4M17 26.8H19.6M44.4 26.8H47"
            stroke="#d97706"
            strokeWidth="1.2"
          />
        </g>
      );
    case "titanium-rect":
      return (
        <g>
          <rect
            x="19.6"
            y="24.4"
            width="11"
            height="7"
            rx="2.4"
            fill="#ffffff"
            fillOpacity="0.08"
            stroke="#475569"
            strokeWidth="1.4"
          />
          <rect
            x="33.4"
            y="24.4"
            width="11"
            height="7"
            rx="2.4"
            fill="#ffffff"
            fillOpacity="0.08"
            stroke="#475569"
            strokeWidth="1.4"
          />
          <path
            d="M30.6 27.1H33.4M17 26.9H19.6M44.4 26.9H47"
            stroke="#475569"
            strokeWidth="1.3"
          />
        </g>
      );
    case "amber-studio":
      return (
        <g>
          <circle
            cx="25.1"
            cy="28"
            r="4.95"
            fill="#f59e0b"
            fillOpacity="0.12"
            stroke="#b45309"
            strokeWidth="1.6"
          />
          <circle
            cx="38.9"
            cy="28"
            r="4.95"
            fill="#f59e0b"
            fillOpacity="0.12"
            stroke="#b45309"
            strokeWidth="1.6"
          />
          <path
            d="M30.1 27.4H33.9M16.8 27.2H20.1M43.9 27.2H47.2"
            stroke="#b45309"
            strokeWidth="1.3"
          />
        </g>
      );
    case "clear-lab-frames":
      return (
        <g>
          <circle
            cx="25.1"
            cy="28"
            r="4.95"
            fill="#e0f2fe"
            fillOpacity="0.14"
            stroke="#475569"
            strokeOpacity="0.75"
            strokeWidth="1.5"
          />
          <circle
            cx="38.9"
            cy="28"
            r="4.95"
            fill="#e0f2fe"
            fillOpacity="0.14"
            stroke="#475569"
            strokeOpacity="0.75"
            strokeWidth="1.5"
          />
          <path
            d="M30.1 27.4H33.9M16.8 27.2H20.1M43.9 27.2H47.2"
            stroke="#475569"
            strokeOpacity="0.75"
            strokeWidth="1.25"
          />
        </g>
      );
    default:
      return null;
  }
}

/**
 * Apple Memoji ear accessories (gold drop earrings, hoops, studio over-ear headphones).
 */
function renderEarAccessories(earAccessory) {
  switch (earAccessory) {
    case "pearl-stud":
      return (
        <g>
          {/* Delicate gold drop earrings matching Apple Memoji reference */}
          <circle cx="16.1" cy="33.2" r="1.05" fill="#f59e0b" />
          <path d="M16.1 34.2V36.2" stroke="#f59e0b" strokeWidth="1.1" strokeLinecap="round" />
          <circle cx="16.1" cy="36.6" r="1.2" fill="#fbbf24" />
          <circle cx="47.9" cy="33.2" r="1.05" fill="#f59e0b" />
          <path d="M47.9 34.2V36.2" stroke="#f59e0b" strokeWidth="1.1" strokeLinecap="round" />
          <circle cx="47.9" cy="36.6" r="1.2" fill="#fbbf24" />
        </g>
      );
    case "gold-hoop":
      return (
        <g stroke="#f59e0b" strokeWidth="1.35" fill="none">
          <circle cx="16" cy="34.2" r="1.65" />
          <circle cx="48" cy="34.2" r="1.65" />
        </g>
      );
    case "silver-stud":
      return (
        <g fill="#94a3b8">
          <circle cx="16.2" cy="33.4" r="1.15" />
          <circle cx="47.8" cy="33.4" r="1.15" />
        </g>
      );
    case "botanical-stud":
      return (
        <g fill="#10b981">
          <circle cx="16.2" cy="33.4" r="1.15" />
          <circle cx="47.8" cy="33.4" r="1.15" />
        </g>
      );
    case "studio-headphones":
      return (
        <g>
          {/* Sleek Apple AirPods Max-style studio ear cups for Software Engineer */}
          <rect x="12.6" y="25.2" width="4.4" height="10.2" rx="2.2" fill="#334155" />
          <rect x="13.4" y="26.4" width="2.4" height="7.8" rx="1.2" fill="#94a3b8" />
          <rect x="47" y="25.2" width="4.4" height="10.2" rx="2.2" fill="#334155" />
          <rect x="48.2" y="26.4" width="2.4" height="7.8" rx="1.2" fill="#94a3b8" />
        </g>
      );
    default:
      return null;
  }
}

/**
 * Compact Apple Memoji neck/collar crop with subtle studio floor shadow.
 */
function renderProfessionCollar(outfit) {
  const studioFloorPath = "M9 66C11.5 54.5 20 50.5 32 50.5C44 50.5 52.5 54.5 55 66H9Z";
  const compactBustPath = "M19.5 65C21.2 55.2 25.8 51.6 32 51.6C38.2 51.6 42.8 55.2 44.5 65H19.5Z";

  switch (outfit) {
    case "architect-turtleneck":
      return (
        <g>
          <path d={studioFloorPath} fill="#C85A32" fillOpacity="0.06" />
          <path d={compactBustPath} fill="#23211F" />
          <path d="M25.8 50.2H38.2L39.2 65H24.8L25.8 50.2Z" fill="#C85A32" />
        </g>
      );
    case "executive-chef-collar":
      return (
        <g>
          <path d={studioFloorPath} fill="#10B981" fillOpacity="0.06" />
          <path d={compactBustPath} fill="#FAF8F5" />
          <path
            d="M25.5 51.2L32 56.2L38.5 51.2"
            stroke="#10B981"
            strokeWidth="1.4"
            strokeLinecap="round"
            fill="none"
          />
        </g>
      );
    case "clinical-coat-scrubs":
      return (
        <g>
          <path d={studioFloorPath} fill="#0284C7" fillOpacity="0.06" />
          <path d={compactBustPath} fill="#FAF8F5" />
          <path d="M26.2 51.2L32 59.5L37.8 51.2H26.2Z" fill="#0284C7" />
        </g>
      );
    case "tech-quarter-zip":
      return (
        <g>
          <path d={studioFloorPath} fill="#6366F1" fillOpacity="0.06" />
          <path d={compactBustPath} fill="#312E81" />
          <path d="M26.8 51.2C29.2 54.2 34.8 54.2 37.2 51.2H26.8Z" fill="#FAF8F5" />
        </g>
      );
    case "technical-work-jacket":
      return (
        <g>
          <path d={studioFloorPath} fill="#D97706" fillOpacity="0.06" />
          <path d={compactBustPath} fill="#1E293B" />
          <path d="M26.2 51.2L32 58.2L37.8 51.2H26.2Z" fill="#D97706" />
        </g>
      );
    case "atelier-silk-collar":
      return (
        <g>
          <path d={studioFloorPath} fill="#E11D48" fillOpacity="0.06" />
          <path d={compactBustPath} fill="#881337" />
          <path d="M25.8 51C28.5 54.8 35.5 54.8 38.2 51L36.5 64H27.5L25.8 51Z" fill="#FDA4AF" />
        </g>
      );
    case "captain-epaulet-suit":
      return (
        <g>
          <path d={studioFloorPath} fill="#1D4ED8" fillOpacity="0.06" />
          <path d={compactBustPath} fill="#0F172A" />
          <path d="M26.5 51.2L32 59.2L37.5 51.2H26.5Z" fill="#FAF8F5" />
          <path d="M30.8 52.5H33.2L33.7 64H30.3L30.8 52.5Z" fill="#1D4ED8" />
        </g>
      );
    case "counsel-bespoke-suit":
      return (
        <g>
          <path d={studioFloorPath} fill="#7C3AED" fillOpacity="0.06" />
          <path d={compactBustPath} fill="#18181B" />
          <path d="M26.5 51.2L32 59.5L37.5 51.2H26.5Z" fill="#FAF8F5" />
          <path d="M30.8 52.5H33.2L33.8 64H30.2L30.8 52.5Z" fill="#7C3AED" />
        </g>
      );
    case "botanist-linen-jacket":
      return (
        <g>
          <path d={studioFloorPath} fill="#16A34A" fillOpacity="0.06" />
          <path d={compactBustPath} fill="#166534" />
          <path d="M26.2 51.2C29 54.8 35 54.8 37.8 51.2H26.2Z" fill="#FEF3C7" />
        </g>
      );
    case "inspector-trench-collar":
      return (
        <g>
          <path d={studioFloorPath} fill="#B45309" fillOpacity="0.06" />
          <path d={compactBustPath} fill="#92400E" />
          <path d="M26.5 51.2L32 58.2L37.5 51.2H26.5Z" fill="#FEF3C7" />
        </g>
      );
    case "broker-navy-blazer":
      return (
        <g>
          <path d={studioFloorPath} fill="#0369A1" fillOpacity="0.06" />
          <path d={compactBustPath} fill="#0C4A6E" />
          <path d="M26.5 51.2L32 59.5L37.5 51.2H26.5Z" fill="#FAF8F5" />
          <path d="M30.8 52.5H33.2L33.7 64H30.3L30.8 52.5Z" fill="#0284C7" />
        </g>
      );
    case "finance-charcoal-suit":
      return (
        <g>
          <path d={studioFloorPath} fill="#0D9488" fillOpacity="0.06" />
          <path d={compactBustPath} fill="#134E4A" />
          <path d="M26.5 51.2L32 59.5L37.5 51.2H26.5Z" fill="#FAF8F5" />
          <path d="M30.8 52.5H33.2L33.7 64H30.3L30.8 52.5Z" fill="#0D9488" />
        </g>
      );
    case "creative-director-mockneck":
      return (
        <g>
          <path d={studioFloorPath} fill="#C026D3" fillOpacity="0.06" />
          <path d={compactBustPath} fill="#18181B" />
          <path d="M25.8 50.2H38.2L39.2 65H24.8L25.8 50.2Z" fill="#86198F" />
        </g>
      );
    case "specialist-chambray-collar":
      return (
        <g>
          <path d={studioFloorPath} fill="#2563EB" fillOpacity="0.06" />
          <path d={compactBustPath} fill="#1E3A8A" />
          <path d="M26.5 51.2C29.2 54.4 34.8 54.4 37.5 51.2H26.5Z" fill="#FAF8F5" />
        </g>
      );
    case "safety-chief-dress-collar":
      return (
        <g>
          <path d={studioFloorPath} fill="#DC2626" fillOpacity="0.06" />
          <path d={compactBustPath} fill="#1E293B" />
          <path d="M26.5 51.2L32 59.2L37.5 51.2H26.5Z" fill="#FAF8F5" />
          <path d="M30.8 52.5H33.2L33.7 64H30.3L30.8 52.5Z" fill="#DC2626" />
        </g>
      );
    case "research-director-coat":
      return (
        <g>
          <path d={studioFloorPath} fill="#0891B2" fillOpacity="0.06" />
          <path d={compactBustPath} fill="#FAF8F5" />
          <path d="M26 51.2C28.8 55 35.2 55 38 51.2L36.5 64H27.5L26 51.2Z" fill="#0891B2" />
        </g>
      );
    case "roaster-denim-apron":
      return (
        <g>
          <path d={studioFloorPath} fill="#9A3412" fillOpacity="0.06" />
          <path d={compactBustPath} fill="#431407" />
          <path d="M26.5 51.2L32 58.2L37.5 51.2H26.5Z" fill="#FED7AA" />
        </g>
      );
    case "hospitality-lapel-suit":
      return (
        <g>
          <path d={studioFloorPath} fill="#9333EA" fillOpacity="0.06" />
          <path d={compactBustPath} fill="#3B0764" />
          <path d="M26.5 51.2L32 59.2L37.5 51.2H26.5Z" fill="#FAF5FF" />
        </g>
      );
    case "professor-tweed-collar":
      return (
        <g>
          <path d={studioFloorPath} fill="#65A30D" fillOpacity="0.06" />
          <path d={compactBustPath} fill="#44403C" />
          <path d="M26.5 51.2L32 59.5L37.5 51.2H26.5Z" fill="#FEF3C7" />
          <path d="M30.8 52.5H33.2L33.7 64H30.3L30.8 52.5Z" fill="#65A30D" />
        </g>
      );
    case "structural-blazer-collar":
    default:
      return (
        <g>
          <path d={studioFloorPath} fill="#EA580C" fillOpacity="0.06" />
          <path d={compactBustPath} fill="#1C1917" />
          <path d="M25.8 50.2H38.2L39.2 65H24.8L25.8 50.2Z" fill="#EA580C" />
        </g>
      );
  }
}

function renderPortraitElements(preset, ids) {
  const { gradId, skinGradId, hairGradId, neckGradId, ringId, clipId } = ids;
  const haloGradId = `${gradId}-halo`;
  return (
    <>
      <defs>
        {/* Soft Scandinavian Studio Gallery Backdrop */}
        <linearGradient
          id={gradId}
          x1="8"
          y1="4"
          x2="56"
          y2="60"
          gradientUnits="userSpaceOnUse"
        >
          <stop offset="0%" stopColor={preset.bgStart} />
          <stop offset="52%" stopColor={preset.bgMid || preset.bgStart} />
          <stop offset="100%" stopColor={preset.bgEnd} />
        </linearGradient>

        {/* Smooth Radial Studio Softbox Halo */}
        <radialGradient
          id={haloGradId}
          cx="32"
          cy="27"
          r="26"
          gradientUnits="userSpaceOnUse"
        >
          <stop offset="0%" stopColor="#ffffff" stopOpacity="0.28" />
          <stop offset="55%" stopColor={preset.accent} stopOpacity="0.08" />
          <stop offset="100%" stopColor={preset.accent} stopOpacity="0" />
        </radialGradient>

        {/* 3D Matte Bisque Skin Illumination (Apple Memoji Subsurface Softness) */}
        <radialGradient
          id={skinGradId}
          cx="29"
          cy="23"
          r="21"
          fx="28"
          fy="20"
          gradientUnits="userSpaceOnUse"
        >
          <stop offset="0%" stopColor={preset.skinLight || preset.skin} />
          <stop offset="62%" stopColor={preset.skin} />
          <stop offset="100%" stopColor={preset.skinShadow} />
        </radialGradient>

        {/* 3D Hair Volume Gradient */}
        <linearGradient
          id={hairGradId}
          x1="20"
          y1="8"
          x2="44"
          y2="42"
          gradientUnits="userSpaceOnUse"
        >
          <stop offset="0%" stopColor={preset.hairHighlight || preset.hairColor} />
          <stop offset="48%" stopColor={preset.hairColor} />
          <stop
            offset="100%"
            stopColor={preset.hairColor === "#c7c4c1" ? "#78716c" : "#0f0d0c"}
          />
        </linearGradient>

        {/* Soft Neck Shadow Gradient */}
        <linearGradient
          id={neckGradId}
          x1="32"
          y1="42"
          x2="32"
          y2="54"
          gradientUnits="userSpaceOnUse"
        >
          <stop offset="0%" stopColor={preset.skinDeep || preset.skinShadow} />
          <stop offset="100%" stopColor={preset.skin} />
        </linearGradient>

        {/* Subtle Studio Hairline Rim */}
        <linearGradient
          id={ringId}
          x1="0"
          y1="0"
          x2="64"
          y2="64"
          gradientUnits="userSpaceOnUse"
        >
          <stop offset="0%" stopColor={preset.accent} stopOpacity="0.35" />
          <stop offset="100%" stopColor={preset.badge} stopOpacity="0.14" />
        </linearGradient>

        <clipPath id={clipId}>
          <rect width="64" height="64" rx="32" />
        </clipPath>
      </defs>

      <g clipPath={`url(#${clipId})`}>
        {/* Studio Gallery background disc */}
        <rect width="64" height="64" rx="32" fill={`url(#${gradId})`} />

        {/* Diffused radial daylight halo behind head */}
        <circle cx="32" cy="27" r="26" fill={`url(#${haloGradId})`} />

        {/* Back hair layer */}
        {renderBackHair(preset.hair, `url(#${hairGradId})`, preset.hairColor)}

        {/* Slender Apple Memoji neck & soft chin cast shadow */}
        <rect
          x="27"
          y="42"
          width="10"
          height="11.5"
          rx="5"
          fill={`url(#${neckGradId})`}
        />
        <ellipse
          cx="32"
          cy="45.6"
          rx="5.4"
          ry="2.1"
          fill={preset.skinDeep || preset.skinShadow}
          fillOpacity="0.42"
        />

        {/* Compact Apple Memoji Upper Collar Crop */}
        {renderProfessionCollar(preset.outfit)}

        {/* Proportional Apple Memoji Ears */}
        <ellipse cx="16.4" cy="30.2" rx="3" ry="4.2" fill={preset.skin} />
        <ellipse cx="16.7" cy="30.2" rx="1.4" ry="2.2" fill={preset.skinShadow} fillOpacity="0.7" />
        <ellipse cx="47.6" cy="30.2" rx="3" ry="4.2" fill={preset.skin} />
        <ellipse cx="47.3" cy="30.2" rx="1.4" ry="2.2" fill={preset.skinShadow} fillOpacity="0.7" />

        {/* Apple Memoji Headshot Face (width=29.6 across x=17.2..46.8, height=34.2 across y=12.2..46.4) */}
        <rect
          x="17.2"
          y="12.2"
          width="29.6"
          height="34.2"
          rx="14.8"
          fill={`url(#${skinGradId})`}
        />
        {/* Soft jawline contour */}
        <path
          d="M18.2 31.8C18.2 40.2 24.2 46.2 32 46.2C39.8 46.2 45.8 40.2 45.8 31.8"
          stroke={preset.skinShadow}
          strokeOpacity="0.32"
          strokeWidth="0.85"
          fill="none"
        />

        {/* Soft Matte Bisque Cheek Warmth */}
        <ellipse
          cx="22.2"
          cy="33.2"
          rx="3"
          ry="1.7"
          fill="#f43f5e"
          fillOpacity="0.11"
        />
        <ellipse
          cx="41.8"
          cy="33.2"
          rx="3"
          ry="1.7"
          fill="#f43f5e"
          fillOpacity="0.11"
        />

        {/* Front Hair Layer */}
        {renderFrontHair(
          preset.hair,
          `url(#${hairGradId})`,
          preset.hairColor,
          preset.hairHighlight || preset.accent,
        )}

        {/* Ear & Head Accessories (Drop Earrings, Hoops, Studio Headphones) */}
        {renderEarAccessories(preset.earAccessory)}

        {/* Expressive Apple Memoji Eyes & Sculpted Brows */}
        {renderStudioEyesAndBrows(
          preset.eyes,
          preset.eyeColor,
          preset.hairColor,
          preset.skinShadow,
        )}

        {/* Simple Rounded 3D Apple Memoji Button Nose */}
        {renderStudioNose(preset.skinShadow, preset.skinDeep)}

        {/* Clean Friendly Mouth & Smooth Facial Hair */}
        {renderFacialHairAndMouth(
          preset.mouth,
          preset.facialHair,
          preset.hairColor,
          preset.lipTone,
          preset.skin,
          preset.skinShadow,
        )}

        {/* Iconic Apple Memoji Eyewear */}
        {renderGlasses(preset.glasses)}

        {/* High-Resolution 512x512 3D Apple Memoji × Matte Bisque Ceramic Studio Portrait */}
        {preset.imageUrl && (
          <image
            href={preset.imageUrl}
            x="0"
            y="0"
            width="64"
            height="64"
            preserveAspectRatio="xMidYMid slice"
          />
        )}
      </g>

      {/* Subtle studio border ring */}
      <rect
        x="0.75"
        y="0.75"
        width="62.5"
        height="62.5"
        rx="31.25"
        stroke={`url(#${ringId})`}
        strokeWidth="1.25"
      />
    </>
  );
}

/**
 * Resolves theme-adaptive avatar colors for a given preset, harmonizing backdrops
 * with terracotta, glass, and semantic palettes across light and dark modes.
 *
 * @param {string|null|undefined} themeName - "terracotta" | "glass" | "semantic"
 * @param {string|null|undefined} mode - "light" | "dark"
 * @param {string|number|null|undefined} presetId - Preset avatar identifier (e.g. "avatar-01")
 * @returns {{ bgGradient: string, rimBorder: string, accentColor: string, bgStart: string, bgMid: string, bgEnd: string, badge: string }}
 */
export function resolvePresetThemeColors(themeName, mode = "dark", presetId) {
  const preset = getPresetAvatar(presetId);
  const normalizedTheme = String(themeName || "").toLowerCase().trim();
  const normalizedMode =
    String(mode || "dark").toLowerCase().trim() === "light" ? "light" : "dark";

  if (normalizedTheme === "terracotta") {
    if (normalizedMode === "light") {
      const bgStart = "#f5ede6";
      const bgMid = "#ebdcd0";
      const bgEnd = "#dec9b8";
      const accentColor = "#c25237";
      const rimBorder = "#c25237";
      const badge = "#9a3412";
      return {
        bgGradient: `linear-gradient(135deg, ${bgStart} 0%, ${bgMid} 52%, ${bgEnd} 100%)`,
        rimBorder,
        accentColor,
        bgStart,
        bgMid,
        bgEnd,
        badge,
      };
    }
    // Terracotta dark
    const bgStart = "#1c1917";
    const bgMid = "#261511";
    const bgEnd = "#141211";
    const accentColor = "#c25237";
    const rimBorder = "#c25237";
    const badge = "#ffd5c8";
    return {
      bgGradient: `linear-gradient(135deg, ${bgStart} 0%, ${bgMid} 52%, ${bgEnd} 100%)`,
      rimBorder,
      accentColor,
      bgStart,
      bgMid,
      bgEnd,
      badge,
    };
  }

  if (normalizedTheme === "glass") {
    if (normalizedMode === "light") {
      const bgStart = "#f1f5f9";
      const bgMid = "#e2e8f0";
      const bgEnd = "#cbd5e1";
      const accentColor = "#6366f1";
      const rimBorder = "rgba(255, 255, 255, 0.85)";
      const badge = "#4f46e5";
      return {
        bgGradient: `linear-gradient(135deg, ${bgStart} 0%, ${bgMid} 52%, ${bgEnd} 100%)`,
        rimBorder,
        accentColor,
        bgStart,
        bgMid,
        bgEnd,
        badge,
      };
    }
    // Glass dark
    const bgStart = "#0a0d14";
    const bgMid = "#151b28";
    const bgEnd = "#07090e";
    const accentColor = "#818cf8";
    const rimBorder = "rgba(255, 255, 255, 0.45)";
    const badge = "#c7d2fe";
    return {
      bgGradient: `linear-gradient(135deg, ${bgStart} 0%, ${bgMid} 52%, ${bgEnd} 100%)`,
      rimBorder,
      accentColor,
      bgStart,
      bgMid,
      bgEnd,
      badge,
    };
  }

  // Semantic theme or default fallback: use the preset's native studio gallery tones & domain accents
  const bgStart = preset.bgStart;
  const bgMid = preset.bgMid || preset.bgStart;
  const bgEnd = preset.bgEnd;
  const accentColor = preset.accent;
  const rimBorder = preset.accent;
  const badge = preset.badge;
  return {
    bgGradient: `linear-gradient(135deg, ${bgStart} 0%, ${bgMid} 52%, ${bgEnd} 100%)`,
    rimBorder,
    accentColor,
    bgStart,
    bgMid,
    bgEnd,
    badge,
  };
}

export function PresetAvatarSvg({
  presetId,
  theme,
  mode,
  className = "h-full w-full",
  title,
  ...props
}) {
  const basePreset = getPresetAvatar(presetId);
  const themeColors = theme
    ? resolvePresetThemeColors(theme, mode, presetId)
    : null;

  const preset = themeColors
    ? {
        ...basePreset,
        bgStart: themeColors.bgStart,
        bgMid: themeColors.bgMid,
        bgEnd: themeColors.bgEnd,
        accent: themeColors.accentColor,
        badge: themeColors.badge,
      }
    : basePreset;

  const rawId = React.useId().replace(/[^a-zA-Z0-9_-]/g, "");
  const ids = {
    gradId: `homy-avatar-bg-${preset.index}-${rawId}`,
    skinGradId: `homy-avatar-skin-${preset.index}-${rawId}`,
    hairGradId: `homy-avatar-hair-${preset.index}-${rawId}`,
    neckGradId: `homy-avatar-neck-${preset.index}-${rawId}`,
    ringId: `homy-avatar-ring-${preset.index}-${rawId}`,
    clipId: `homy-avatar-clip-${preset.index}-${rawId}`,
  };

  return (
    <svg
      viewBox="0 0 64 64"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      role="img"
      aria-label={title || `${preset.name} (${preset.profession})`}
      data-preset-avatar={preset.id}
      data-preset-profession={preset.profession}
      {...props}
    >
      {renderPortraitElements(preset, ids)}
    </svg>
  );
}

/**
 * Generates an inline SVG data URI for places where a raw `<img src="..." />` string is required.
 */
export function getAvatarDataUri(presetId) {
  const preset = getPresetAvatar(presetId);
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64" fill="none"><defs><linearGradient id="g" x1="8" y1="4" x2="56" y2="60" gradientUnits="userSpaceOnUse"><stop offset="0%" stop-color="${preset.bgStart}"/><stop offset="52%" stop-color="${preset.bgMid || preset.bgStart}"/><stop offset="100%" stop-color="${preset.bgEnd}"/></linearGradient><radialGradient id="s" cx="29" cy="23" r="21" fx="28" fy="20" gradientUnits="userSpaceOnUse"><stop offset="0%" stop-color="${preset.skinLight || preset.skin}"/><stop offset="62%" stop-color="${preset.skin}"/><stop offset="100%" stop-color="${preset.skinShadow}"/></radialGradient><linearGradient id="h" x1="20" y1="8" x2="44" y2="42" gradientUnits="userSpaceOnUse"><stop offset="0%" stop-color="${preset.hairHighlight || preset.hairColor}"/><stop offset="48%" stop-color="${preset.hairColor}"/><stop offset="100%" stop-color="${preset.hairColor === "#c7c4c1" ? "#78716c" : "#0f0d0c"}"/></linearGradient><linearGradient id="n" x1="32" y1="42" x2="32" y2="54" gradientUnits="userSpaceOnUse"><stop offset="0%" stop-color="${preset.skinDeep || preset.skinShadow}"/><stop offset="100%" stop-color="${preset.skin}"/></linearGradient><clipPath id="c"><rect width="64" height="64" rx="32"/></clipPath></defs><g clip-path="url(#c)"><rect width="64" height="64" rx="32" fill="url(#g)"/><circle cx="32" cy="28" r="23" fill="#ffffff" fill-opacity="0.35"/><path d="M16.2 26.5C15.8 13.8 22.2 7.8 32 7.8C41.8 7.8 48.2 13.8 47.8 26.5H16.2Z" fill="url(#h)"/><rect x="26.8" y="41.8" width="10.4" height="11.5" rx="5" fill="url(#n)"/><path d="M9 66C11.5 54.5 20 50.5 32 50.5C44 50.5 52.5 54.5 55 66H9Z" fill="#23211F"/><ellipse cx="16.4" cy="30.2" rx="3" ry="4.2" fill="${preset.skin}"/><ellipse cx="47.6" cy="30.2" rx="3" ry="4.2" fill="${preset.skin}"/><rect x="17.2" y="12.2" width="29.6" height="34.2" rx="14.8" fill="url(#s)"/><path d="M16.4 27C16.2 14 22.4 7.8 32 7.8C41.6 7.8 47.8 14 47.6 27L45.4 30.6C44.5 21.5 39.2 16.6 29.8 16.6C24 16.6 19.4 21.2 18.6 30.6L16.4 27Z" fill="url(#h)"/><ellipse cx="25.4" cy="27.6" rx="3.65" ry="2.65" fill="#ffffff"/><ellipse cx="38.6" cy="27.6" rx="3.65" ry="2.65" fill="#ffffff"/><circle cx="25.45" cy="27.65" r="2.15" fill="${preset.eyeColor || "#3b281c"}"/><circle cx="38.65" cy="27.65" r="2.15" fill="${preset.eyeColor || "#3b281c"}"/><circle cx="25.45" cy="27.65" r="1.08" fill="#12100e"/><circle cx="38.65" cy="27.65" r="1.08" fill="#12100e"/><circle cx="24.75" cy="26.88" r="0.74" fill="#ffffff"/><circle cx="37.95" cy="26.88" r="0.74" fill="#ffffff"/><path d="M21.5 27.4C22.7 24.8 28.1 24.8 29.3 27.4M34.7 27.4C35.9 24.8 41.3 24.8 42.5 27.4" stroke="#1c1613" stroke-width="1.2" stroke-linecap="round"/><path d="M21.4 22.3C23.6 21 27 21 29.1 22.1M34.9 22.1C37 21 40.4 21 42.6 22.3" stroke="${preset.hairColor === "#c7c4c1" ? "#78716c" : preset.hairColor}" stroke-width="1.75" stroke-linecap="round"/><path d="M30.1 33.4C31.1 34.3 32.9 34.3 33.9 33.4" stroke="${preset.skinDeep || preset.skinShadow}" stroke-opacity="0.52" stroke-width="1.35" stroke-linecap="round"/><path d="M27.8 38.5C29.6 40.3 34.4 40.3 36.2 38.5C35.2 40.3 33.8 41 32 41C30.2 41 28.8 40.3 27.8 38.5Z" fill="${preset.lipTone || "#bd6b5a"}" fill-opacity="0.72"/><path d="M27.4 38.3C29.5 40.2 34.5 40.2 36.6 38.3" stroke="#6e3429" stroke-width="1.45" stroke-linecap="round"/></g></svg>`;
  return `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`;
}
